/**
 * Engine Atlas field classification maps per scope §4 layer 6 path a +
 * scope §8 follow-ups.
 *
 * Three orthogonal classification axes per scope §6 layers 5/6/9:
 *   - layer5Nature: mechanical vs strategic
 *   - layer6Authority: operational vs ownership (optional per three-state
 *     layer applicability principle — absent = surface at region-altitude
 *     for this field, not per-value-altitude)
 *   - layer9Reversibility: clean-reversible / reversible-with-residue /
 *     structurally-irreversible
 *
 * Classifications don't covary across axes — see scope §4.6 layer
 * applicability + Atlas Day 2 build prompt §1 three-axis discipline reminder.
 * A value can be mechanical + N/A + clean-reversible (typical for externally-
 * determined facts) OR strategic + ownership + reversible-with-residue
 * (typical for stance-defining configuration) OR any other combination.
 *
 * Atlas-code-level classification per scope §4 layer 6 path a. Engine
 * Editor dedicated session may migrate to substrate-backed classification
 * per path b — scope §8 follow-up.
 *
 * Scope per Atlas Day 2 Phase 0 #17/#18 + Sol D-locks 2026-05-27:
 *   - distributors (D6 lock: 5 build-prompt + 2 substrate additions
 *     estimated_tax_pct + marketplace_permissions; skip code/email)
 *   - suppliers (4 fields; empty table — classifications carry forward)
 *   - brands (D2/D7 lock: 4 build-prompt + 3 substrate additions; skip
 *     map_price per #11.6 scope-doc clarification — column lives on
 *     products not brands; skip FK columns per D7)
 *   - manufacturers + manufacturer_distributors (sparse 1×1 today)
 *   - categories (empty table; placeholder classifications)
 *   - params at subsection altitude (per Day 86 A6 PARAM_GROUPS_SETTINGS)
 *   - cost_line_items (5 fields per Phase 0 #8 actual columns;
 *     display_name not name)
 *   - fulfillment_path_config (6 fields per Phase 0 #9)
 *   - buy_score_weight_components (7 fields; weight excluded per D1 (α)
 *     lock — weights live in buying_sessions.ui_state.weights per-session)
 *
 * 3 Phase 1 chat-ambiguity items resolved by Sol direction 2026-05-27:
 *   - distributors.estimated_tax_pct: CONFIRMED mechanical + N/A + clean-reversible
 *   - brands.gating_status: CONFIRMED mechanical + N/A + clean-reversible (vestigial rationale)
 *   - manufacturer_distributors.pairing_existence: AMENDED ownership → operational
 *     (parallel to distributor.is_active operational classification)
 */

type Layer5Nature = 'mechanical' | 'strategic';
type Layer6Authority = 'operational' | 'ownership';
type Layer9Reversibility =
  | 'clean-reversible'
  | 'reversible-with-residue'
  | 'structurally-irreversible';

/** @public — root-tests-import-apps (U11 census C1, corrected at S2): consumed by the root suite (atlasDay2ClassificationMaps.test.ts:20); the census's de-export lane was a name-twin misresolution vs FieldClassificationMap, caught by the tsc net. */
export interface FieldClassification {
  layer5Nature: Layer5Nature;
  /**
   * Absent = three-state layer applicability principle (scope §4.6); layer 6
   * inspect surfaces region-altitude content for this field rather than
   * per-value-altitude. Typical for externally-determined facts (regulatory,
   * distributor-reported, structural) where operational vs ownership
   * distinction doesn't apply at value-altitude.
   */
  layer6Authority?: Layer6Authority;
  layer9Reversibility: Layer9Reversibility;
  /**
   * Optional inspect-content framing or classification rationale. Use for:
   *   - documenting the "why" behind a classification (especially when not
   *     obvious from the field name)
   *   - surfacing scope-conversation ambiguities (PROPOSED markers for
   *     Phase 1 chat-ambiguity items pending Sol amendment)
   *   - vestigial-state markers for build-history reference
   *   - region-altitude inspect copy when layer6Authority is absent
   */
  rationale?: string;
}

type TableClassifications = Record<string, FieldClassification>;
export type FieldClassificationMap = Record<string, TableClassifications>;

export const FIELD_CLASSIFICATIONS: FieldClassificationMap = {
  distributors: {
    credit_limit: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: "defines ISG's credit-extension stance per distributor",
    },
    payment_terms: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: "defines ISG's payment-terms stance per distributor",
    },
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle',
    },
    tax_exempt: {
      layer5Nature: 'mechanical',
      // layer6Authority absent — regulatory fact (resale-certificate state);
      // no operational/ownership distinction at value-altitude
      layer9Reversibility: 'clean-reversible',
      rationale:
        'regulatory fact (resale-certificate state); layer 6 N/A — surfaces region-altitude inspect content per three-state layer applicability',
    },
    name: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'distributor identity',
    },
    estimated_tax_pct: {
      // CONFIRMED Sol 2026-05-27 — mechanical regulatory rate ISG records,
      // not stance ISG chooses
      layer5Nature: 'mechanical',
      // layer6Authority absent — Day 55 substrate; tracks regional reality
      layer9Reversibility: 'clean-reversible',
      rationale:
        'regional tax rate ISG records per distributor relationship; mechanical fact substrate.',
    },
    marketplace_permissions: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'cross-domain permission stance per distributor',
    },
  },
  suppliers: {
    lead_time_days: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'clean-reversible',
      rationale: 'supplier-reported lead time fact',
    },
    moq: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'clean-reversible',
      rationale: 'supplier-set minimum order quantity',
    },
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle',
    },
    name: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'supplier identity',
    },
  },
  brands: {
    enforces_map: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: "defines ISG's MAP-enforcement stance per brand (per scope §6.6.1 cross-table substrate)",
    },
    auto_seeded: {
      layer5Nature: 'mechanical',
      // layer6Authority absent — Algopix-discovery flag; not stance-defining
      layer9Reversibility: 'clean-reversible',
      rationale: 'Algopix-discovery flag (mechanical fact); layer 6 N/A',
    },
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle for buying',
    },
    gating_status: {
      // CONFIRMED Sol 2026-05-27 — vestigial classification today; revisit
      // if substrate gains per-brand variance
      layer5Nature: 'mechanical',
      // layer6Authority absent — currently no per-brand variance
      layer9Reversibility: 'clean-reversible',
      rationale:
        'classification reflects current uniform-state across 911 brands (all "open"); revisit if substrate gains per-brand variance.',
    },
    return_policy: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'per-brand return-handling stance',
    },
    restocking_fee_pct: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'per-brand restocking-fee stance',
    },
    marketplace_permissions: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'cross-domain permission stance per brand',
    },
  },
  manufacturers: {
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle',
    },
    name: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'manufacturer identity',
    },
  },
  manufacturer_distributors: {
    pairing_existence: {
      // AMENDED Sol 2026-05-27 — operational, not ownership. Parallel to
      // distributor.is_active operational classification.
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale:
        "pairing toggles read operationally — ISG's manufacturer-distributor network evolves as operating reality; strategic-altitude manufacturer-network framing lives at manufacturer entity layer, not per-pairing.",
    },
    discount_pct: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'per-pairing discount stance',
    },
  },
  categories: {
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle (currently sparse — Phase 0 #5 verified empty; substrate populates per Day 88 CF 64)',
    },
    name: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'category identity (currently sparse — Phase 0 #5 verified empty; substrate populates per Day 88 CF 64)',
    },
  },
  // ── business-level params at subsection altitude ───────────────────────────
  // Per Day 86 A6 PARAM_GROUPS_SETTINGS (src/pages/Settings.tsx). Subsection
  // labels imported cross-app at Phase 4 Business defaults region per ADR 36
  // sibling-apps pattern. Per-row classification (33 keys) deferred — subsection
  // altitude is the granularity per Atlas Day 2 build prompt §1 ref cases.
  params: {
    engine_tuning: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'change-with-care params (per Day 86 A6 Engine Tuning subsection — 2 surviving keys)',
    },
    fulfillment_costs: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'routine-tunable params (per Day 86 A6 Fulfillment Costs subsection — L467 cluster). Per-row review possible for layer 5 mechanical exceptions.',
    },
    margins: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'profitability-threshold params (min_roi_pct, min_margin_pct, etc.)',
    },
    returns: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'returns-handling stance params (returns_reserve_pct, default_returns_rate, etc.)',
    },
    credit_thresholds: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'credit-allocation alert thresholds (credit_util_warning_pct, credit_util_danger_pct, credit_available_danger_pct)',
    },
    marketplace_fees: {
      layer5Nature: 'mechanical',
      // layer6Authority absent — marketplace-set fee rates; tracks marketplace reality, not ISG stance
      layer9Reversibility: 'clean-reversible',
      rationale: 'marketplace-set fee rates (ebay_fee_rate, walmart_referral_fee_rate, shopify_payment_fee_rate, etc.) — tracks marketplace reality',
    },
    timing: {
      layer5Nature: 'mechanical',
      // layer6Authority absent — payout cycles + staleness thresholds (mostly external facts)
      layer9Reversibility: 'clean-reversible',
      rationale: 'payout cycle days + staleness thresholds (mostly external facts: amazon_payout_days, walmart_payout_days, manual_override_stale_days, etc.)',
    },
  },
  cost_line_items: {
    calculation_type: {
      layer5Nature: 'mechanical',
      // layer6Authority absent — structural enum
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'structural enum (per_unit_usd, per_order, percentage, etc.); engine branches on this',
    },
    value: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'cost-component dollar value',
    },
    display_name: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'cost-component admin-facing label (per Phase 0 #8 — actual column is display_name not name)',
    },
    key: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'storage key (engine reference); structural identity',
    },
    applies_to_paths: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'path-applicability array (structural mapping; engine branches by inclusion)',
    },
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle',
    },
  },
  fulfillment_path_config: {
    path_key: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'path identity (engine reference; structural)',
    },
    path_type: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'schema-shape (merchant_managed / fulfillment_service_managed / dropship); Day 86 A3 substrate',
    },
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle',
    },
    applies_inbound_shipping: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'path metadata flag (engine branches on this; Day 66 P2.1 substrate)',
    },
    applies_prep_fee: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'path metadata flag (engine branches on this; Day 66 P2.1 substrate)',
    },
    payout_param_key: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'path metadata flag (engine reads payout days from this param key reference)',
    },
  },
  // bswc.weight excluded per D1 (α) final lock 2026-05-27 — weights live in
  // buying_sessions.ui_state.weights (per-session/buyer-private scenario state),
  // not engine substrate. Atlas surfaces bswc catalog (engine state) only;
  // per-session weighting stays scenario state per scope §2.
  buy_score_weight_components: {
    label: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'metric display label (admin-curated)',
    },
    key: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'engine reference key; structural identity',
    },
    is_available: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'metric availability gate for buyer selection (engine catalog admission)',
    },
    applies_to_metric: {
      layer5Nature: 'mechanical',
      layer9Reversibility: 'structurally-irreversible',
      rationale: 'engine metric mapping (structural; engine reads this to dispatch CASE per Day 68 Q1 Path B)',
    },
    sort_order: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'display order in admin + buyer UI headers',
    },
    tooltip_text: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'admin-curated tooltip for buyer-facing headers (Day 91a P2A substrate)',
    },
    penalty: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'reversible-with-residue',
      rationale: 'per-metric penalty boolean (engine negates weight on calculation per Day 68 Q1)',
    },
  },
  // ── Atlas Day 3a P1 — D-3 lock: marketplaces classification add ────────────
  // Per Sol D-3 lock 2026-05-27 (docs/atlas-day-3a-sol-locks.md): 2 fields
  // for Path eligibility rule region L6 framing. Mirrors distributor.is_active
  // operational + cost_line_items.display_name ownership patterns.
  marketplaces: {
    is_active: {
      layer5Nature: 'strategic',
      layer6Authority: 'operational',
      layer9Reversibility: 'clean-reversible',
      rationale: 'operating availability toggle per marketplace (parallel to distributor.is_active pattern)',
    },
    display_name: {
      layer5Nature: 'strategic',
      layer6Authority: 'ownership',
      layer9Reversibility: 'clean-reversible',
      rationale: 'marketplace identity label (parallel to cost_line_items.display_name pattern)',
    },
  },
};

/**
 * Phase 1 chat-ambiguity items resolved by Sol direction 2026-05-27.
 * Historical record — all 3 items now have CONFIRMED or AMENDED
 * classifications in FIELD_CLASSIFICATIONS above:
 *
 *   distributors.estimated_tax_pct        — CONFIRMED mechanical + N/A + clean-reversible
 *   brands.gating_status                  — CONFIRMED mechanical + N/A + clean-reversible (vestigial rationale)
 *   manufacturer_distributors.pairing_existence — AMENDED ownership → operational (parallel to distributor.is_active)
 *
 * Array retained for downstream consumers + tests; emptied to signal
 * "no current ambiguities pending Sol direction" state.
 */
/** @public — root-tests-import-apps (U11 census C1): consumed by the root suite (atlasDay2ClassificationMaps.test.ts). */
export const CHAT_AMBIGUITY_ITEMS = [] as const;
